import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import type { Destination } from "@ckgo2/shared/types";
import { Plus, Search, MapPin, Edit, Trash2, Star } from "lucide-react";
import clsx from "clsx";

const CONTINENTS: Record<string, string> = {
  asia: "Asie",
  africa: "Afrika",
  "south-america": "Jižní Amerika",
  "north-america": "Severní Amerika",
  oceania: "Oceánie",
  europe: "Evropa",
};

export default function DestinationsPage() {
  const [search, setSearch] = useState("");
  const [continentFilter, setContinentFilter] = useState<string>("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["destinations"],
    queryFn: () =>
      api.get<ApiResponse<Destination[]>>("/api/destinations"),
  });

  const destinations = data?.data || [];

  // Filter destinations
  const filteredDestinations = destinations.filter((dest) => {
    const matchesSearch =
      dest.name.toLowerCase().includes(search.toLowerCase()) ||
      dest.description?.toLowerCase().includes(search.toLowerCase());
    const matchesContinent =
      !continentFilter || dest.continent === continentFilter;
    return matchesSearch && matchesContinent;
  });

  if (error) {
    return (
      <div className="card text-center text-error-600">
        Chyba při načítání destinací. Zkuste to prosím znovu.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Destinace
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            Spravujte destinace zájezdů
          </p>
        </div>
        <Link to="/destinations/new" className="btn-primary">
          <Plus className="h-5 w-5" />
          Nová destinace
        </Link>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col gap-4 sm:flex-row">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Hledat destinace..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input pl-10"
            />
          </div>

          {/* Continent filter */}
          <select
            value={continentFilter}
            onChange={(e) => setContinentFilter(e.target.value)}
            className="input w-full sm:w-48"
          >
            <option value="">Všechny kontinenty</option>
            {Object.entries(CONTINENTS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden p-0">
        {isLoading ? (
          <div className="flex h-64 items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
          </div>
        ) : filteredDestinations.length === 0 ? (
          <div className="flex h-64 flex-col items-center justify-center text-gray-500">
            <MapPin className="mb-2 h-12 w-12 text-gray-300" />
            <p>Žádné destinace nenalezeny</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                  Destinace
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                  Kontinent
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                  Pořadí
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                  Status
                </th>
                <th className="px-6 py-4 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                  Akce
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredDestinations.map((destination) => (
                <tr
                  key={destination.id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-800/50"
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      {destination.featuredImage ? (
                        <img
                          src={destination.featuredImage}
                          alt={destination.name}
                          className="h-10 w-10 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-700">
                          <MapPin className="h-5 w-5 text-gray-400" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {destination.name}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          /{destination.slug}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-gray-600 dark:text-gray-300">
                      {CONTINENTS[destination.continent]}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-gray-600 dark:text-gray-300">
                      {destination.sortOrder}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {destination.isFeatured ? (
                      <span className="badge-primary inline-flex items-center gap-1">
                        <Star className="h-3 w-3" />
                        Featured
                      </span>
                    ) : (
                      <span className="badge bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                        Standard
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <Link
                        to={`/destinations/${destination.id}`}
                        className="btn-ghost btn-sm"
                      >
                        <Edit className="h-4 w-4" />
                      </Link>
                      <button className="btn-ghost btn-sm text-error-600 hover:bg-error-50 hover:text-error-700 dark:text-error-400 dark:hover:bg-error-900/20">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
